import { raundColors } from '@/theme/colors'
import { AccordionChevron, ActionIcon, NativeSelect, PasswordInput, TextInput } from '@mantine/core'
import { IconSearch, IconX } from '@tabler/icons-react'
import type React from 'react'
import { useState } from 'react'
import styles from './Input.module.css'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type InputMode = 'text' | 'password' | 'select' | 'search'

export type InputStatus = 'normal' | 'success' | 'error' | 'readonly' | 'disabled'

export type SelectOption = { value: string; label: string } | string

export type InputProps = {
  mode?: InputMode
  status?: InputStatus
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  onClear?: () => void
  placeholder?: string
  /** select モード用の選択肢 */
  selectOptions?: SelectOption[]
  /** search モード用のサイズ: middle=48px, small=36px */
  size?: 'middle' | 'small'
  style?: React.CSSProperties
  className?: string
  id?: string
  name?: string
}

// ─────────────────────────────────────────────
// ステータス別 CSS 変数
// ─────────────────────────────────────────────
const STATUS_VARS: Record<InputStatus, Record<string, string>> = {
  normal: {
    '--input-bd': raundColors.black,
    '--input-bg': raundColors.white,
    '--input-color': raundColors.black,
  },
  success: {
    '--input-bd': raundColors.success.base,
    '--input-bg': raundColors.white,
    '--input-color': raundColors.black,
  },
  error: {
    '--input-bd': raundColors.danger.base,
    '--input-bg': raundColors.white,
    '--input-color': raundColors.black,
  },
  readonly: {
    '--input-bd': raundColors.black,
    '--input-bg': raundColors.system.tint,
    '--input-color': raundColors.black,
  },
  disabled: {
    '--input-bd': raundColors.black,
    '--input-bg': raundColors.system.tint,
    '--input-color': raundColors.system.base,
  },
}

// Figma: height=36px, border-radius=8px
const BASE_WRAPPER_VARS = {
  '--input-radius': '8px',
}

// ─────────────────────────────────────────────
// 検索モード用アイコン
// ─────────────────────────────────────────────
const SEARCH_SIZE_CONFIG = {
  middle: { height: 48, iconSize: 16, fontSize: 16 },
  small: { height: 36, iconSize: 14, fontSize: 16 },
} as const

const SearchIcon = ({ size }: { size: number }) => (
  <IconSearch size={size} stroke={2} color={raundColors.primary.base} aria-hidden="true" />
)

const ClearIcon = ({ size }: { size: number }) => (
  <IconX size={size} stroke={2} color={raundColors.black} aria-hidden="true" />
)

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
export const Input = ({
  mode = 'text',
  status = 'normal',
  value: controlledValue,
  defaultValue,
  onChange,
  onBlur,
  onClear,
  placeholder,
  selectOptions = [],
  size = 'middle',
  style,
  className,
  id,
  name,
}: InputProps) => {
  const isReadOnly = status === 'readonly'
  const isDisabled = status === 'disabled'

  // ── 検索モード ──────────────────────────────
  const [searchInternalValue, setSearchInternalValue] = useState(defaultValue ?? '')
  if (mode === 'search') {
    const sc = SEARCH_SIZE_CONFIG[size]
    const isControlled = controlledValue !== undefined
    const currentValue = isControlled ? controlledValue : searchInternalValue

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget.value
      if (!isControlled) setSearchInternalValue(val)
      onChange?.(val)
    }

    const handleClear = () => {
      if (!isControlled) setSearchInternalValue('')
      onChange?.('')
      onClear?.()
    }

    return (
      <TextInput
        id={id}
        name={name}
        value={currentValue}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder ?? 'キーワードで検索'}
        style={style}
        className={className}
        readOnly={isReadOnly}
        disabled={isDisabled}
        leftSection={<SearchIcon size={sc.iconSize} />}
        rightSection={
          currentValue && !isDisabled && !isReadOnly ? (
            <ActionIcon variant="transparent" onClick={handleClear} aria-label="クリア" size={sc.iconSize + 8}>
              <ClearIcon size={sc.iconSize} />
            </ActionIcon>
          ) : null
        }
        styles={{
          wrapper: {
            '--input-radius': '8px',
            '--input-height': `${sc.height}px`,
            ...STATUS_VARS[status],
            // search モードは normal 時も黄色ボーダー（ブランドカラー）
            ...(status === 'normal' && { '--input-bd': raundColors.primary.base }),
          } as React.CSSProperties,
          // iOS Safari の自動ズーム防止のため font-size を 16px に統一する
          input: { fontSize: `${sc.fontSize}px` } as React.CSSProperties,
        }}
        classNames={{ root: styles.root }}
      />
    )
  }

  // ── Mantine ベースモード ─────────────────────
  const commonProps = {
    // size='sm' → Mantine デフォルトで height=36px
    size: 'sm' as const,
    styles: {
      wrapper: { ...BASE_WRAPPER_VARS, ...STATUS_VARS[status] } as React.CSSProperties,
      // iOS Safari の自動ズーム防止のため font-size を 16px に統一する（元 Figma 仕様: 12px）
      input: { fontSize: '16px' } as React.CSSProperties,
    },
    style,
    className,
    placeholder,
    readOnly: isReadOnly,
    disabled: isDisabled,
    id,
    name,
    classNames: { root: styles.root },
  }

  if (mode === 'password') {
    return (
      <PasswordInput
        {...commonProps}
        value={controlledValue}
        defaultValue={defaultValue}
        onChange={(e) => onChange?.(e.currentTarget.value)}
        onBlur={onBlur}
      />
    )
  }

  if (mode === 'select') {
    return (
      <NativeSelect
        {...commonProps}
        value={controlledValue}
        defaultValue={defaultValue}
        onChange={(e) => onChange?.(e.currentTarget.value)}
        onBlur={onBlur}
        data={selectOptions}
        rightSection={<AccordionChevron size={12} />}
      />
    )
  }

  return (
    <TextInput
      {...commonProps}
      value={controlledValue}
      defaultValue={defaultValue}
      onChange={(e) => onChange?.(e.currentTarget.value)}
      onBlur={onBlur}
    />
  )
}
